import type { WatchSource } from "vue";
import { ref, watch } from "vue";
import type { ReadLoanOffer } from "@/generatedApi";

/**
 * Splits a HDN value into two parts.
 *
 * @param value The HDN value to split.
 * @returns A tuple containing the key and value parts of the HDN value.
 */
export function splitHdnValue(value?: string | null): [string, string] {
	if (!value) {
		return ["", ""];
	}

	const splitIdx = value.indexOf(" ");
	return [value.substring(0, splitIdx), value.substring(splitIdx + 1)];
}

/**
 * Splits a reactive HDN value into two parts, reactively
 *
 * @param value The HDN value to split, provided as a WatchSource
 * @returns A tuple containing two refs for the key and value parts of the HDN value.
 */
export function useSplitHdnValue(value: WatchSource<string>) {
	const key = ref("");
	const val = ref("");

	watch(
		value,
		(updatedVal) => {
			[key.value, val.value] = splitHdnValue(updatedVal);
		},
		{
			immediate: true,
		},
	);

	return [key, val];
}

export function getLoanOfferName(
	loanOfferOrType: string | ReadLoanOffer | null,
) {
	const type =
		typeof loanOfferOrType === "string"
			? loanOfferOrType
			: loanOfferOrType?.type;
	if (!type) {
		return "";
	}

	if (type === "preliminary") {
		return "Renteaanbod";
	}
	if (type === "final") {
		return "Definitieve offerte";
	}

	return "";
}

export function hdnListToOptions<T extends string>(
	values: T[] | Record<T, T>,
	{ shouldSort = false }: { shouldSort?: boolean },
) {
	const parsedOptions = (
		!Array.isArray(values) ? Object.keys(values) : values
	).map((option) => {
		const [, value] = splitHdnValue(option);
		return {
			value: option,
			label: value,
		};
	});

	if (shouldSort) {
		parsedOptions.sort((a, b) => a.label.localeCompare(b.label));
	}

	return parsedOptions;
}
